import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EnvelopeSimple, WhatsappLogo, CheckCircle, ClipboardText, MapPin } from "@phosphor-icons/react"
import { TrialApplicationForm } from "@/components/TrialApplicationForm"

export function ContactPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory via-ivory to-muted">
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-navy mb-6">
              ⚽ Football Trials & Contact
            </h1>
            <p className="text-xl text-navy-muted max-w-2xl mx-auto">
              Begin your football journey with ZOE Football Academy
            </p>
            <div className="mt-8">
              <img 
                src="/images/hero-bg.jpg" 
                alt="Football trials" 
                className="rounded-lg shadow-lg mx-auto"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            <div className="space-y-8">
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <ClipboardText className="text-gold" size={32} weight="duotone" />
                  <h2 className="font-serif text-3xl font-semibold text-navy">Apply for Trials</h2>
                </div>
                <Card className="border-gold/20">
                  <CardContent className="pt-6">
                    <p className="text-navy-muted mb-6">
                      ZOE Football Academy conducts periodic football trials and evaluation camps.
                    </p>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-navy text-sm uppercase tracking-wider">
                        Players are assessed on:
                      </h4>
                      {[
                        "Technical ability",
                        "Game intelligence",
                        "Discipline and attitude",
                        "Long‑term potential"
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={18} weight="fill" />
                          <span className="text-navy-muted text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>

              <section>
                <h3 className="font-serif text-2xl font-semibold text-navy mb-4">
                  Registration Process
                </h3>
                <div className="space-y-3">
                  {[
                    { num: "1", text: "Submit interest form" },
                    { num: "2", text: "Initial screening" },
                    { num: "3", text: "Trial camp invitation" },
                    { num: "4", text: "Evaluation & feedback" }
                  ].map((step) => (
                    <div key={step.num} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                        <span className="text-navy font-semibold text-sm">{step.num}</span>
                      </div>
                      <span className="text-navy-muted">{step.text}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="font-serif text-2xl font-semibold text-navy mb-4">
                  Contact Information
                </h3>
                {/* Nigeria Contact */}
                <Card className="border-gold/20 mb-4">
                  <CardContent className="pt-6">
                    <h4 className="text-gold font-semibold mb-4 flex items-center gap-2">
                      🇳🇬 Nigeria
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <EnvelopeSimple className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">Email</div>
                          <a href="mailto:nigeria@zoeventures.one" className="text-navy hover:text-gold transition-colors">
                            nigeria@zoeventures.one
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <WhatsappLogo className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">WhatsApp</div>
                          <a href="https://wa.me/2348135091808" className="text-navy hover:text-gold transition-colors">
                            +234 813 509 1808
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">Location</div>
                          <span className="text-navy">Suleja Township Stadium, Nigeria</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* India Contact */}
                <Card className="border-gold/20">
                  <CardContent className="pt-6">
                    <h4 className="text-gold font-semibold mb-4 flex items-center gap-2">
                      🇮🇳 India
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <EnvelopeSimple className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">Email</div>
                          <a href="mailto:india@zoeventures.one" className="text-navy hover:text-gold transition-colors">
                            india@zoeventures.one
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <WhatsappLogo className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">WhatsApp</div>
                          <a href="https://wa.me/917348958352" className="text-navy hover:text-gold transition-colors">
                            +91 73489 58352
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="text-gold flex-shrink-0 mt-1" size={20} weight="duotone" />
                        <div>
                          <div className="text-xs text-navy-muted uppercase tracking-wider mb-1">Location</div>
                          <span className="text-navy">India Office</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </section>
            </div>

            <div>
              <Card className="border-gold/20 sticky top-24">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl text-navy">
                    Trial Application Form
                  </CardTitle>
                  <p className="text-sm text-navy-muted">
                    Complete the form below to express your interest
                  </p>
                </CardHeader>
                <CardContent>
                  <TrialApplicationForm formType="Trial Application" />
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="text-center py-8 px-6 rounded-lg bg-gradient-to-r from-navy via-navy-muted to-navy">
            <p className="text-ivory text-lg font-serif font-semibold">
              Discipline. Development. Destiny.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
